import { Injectable } from '@angular/core';
import { filter, take } from 'rxjs/operators';
import { ObservableScope } from '../models/observable-scope.enum';
import { ConfigParserService } from '../services/config-parser.service';
import { ConsumerSkuInfo, ConsumerSkuType } from '../services/consumer-license.service';
import { EventService } from '../services/event.service';
import { DOMInteractiveMarker } from '../utilities/dom-interactive-marker.utility';
import { AngularEvents } from './angular-events';
import { CapabilitiesService } from './capabilities.service';
import { AuthType, ClientStartupConfigService } from './client-startup-config.service';

export interface ConsumerInstallConfig {
    currentSkuInfo: ConsumerSkuInfo;
    expiredSkuInfo: ConsumerSkuInfo;
}

@Injectable({
    providedIn: 'root'
})
export class ConsumerInstallConfigService {
    public static readonly consumerInstallConfigElementId = 'consumerInstallInfo';

    private configPromise: Promise<ConsumerInstallConfig>;
    private refreshedConfig: Promise<ConsumerInstallConfig>;

    constructor(
        private readonly capabilitiesService: CapabilitiesService,
        private readonly configParserService: ConfigParserService,
        private readonly eventService: EventService,
        private readonly startupConfigService: ClientStartupConfigService
    ) {
        this.eventService.events.withScope(ObservableScope.OnlyFuture)
        .pipe(filter(event => event.name === AngularEvents.convertedToPaid), take(1))
        .toPromise()
        .then(() => {
            this.refreshedConfig = this.configPromise.then(config => {
                if (config) {
                    return {
                        currentSkuInfo: { skuType: ConsumerSkuType.O365Personal, daysExpired: -365 },
                        expiredSkuInfo: config.expiredSkuInfo
                    };
                }

                return {
                    currentSkuInfo: { skuType: ConsumerSkuType.NotDetected, daysExpired: -1 },
                    expiredSkuInfo: { skuType: ConsumerSkuType.NotDetected, daysExpired: -1 }
                };
            });
        });
    }

    // It is possible we don't have the 3rd chunk with consumer info and tokens yet, so we may need to call this in the code where we use config
    public getConfig(): Promise<ConsumerInstallConfig> {
        if (this.refreshedConfig) {
            return this.refreshedConfig;
        }

        if (!this.configPromise) {
            if (this.startupConfigService.config.authType !== AuthType.MSA) {
                return Promise.resolve({
                    currentSkuInfo: { skuType: ConsumerSkuType.NotDetected, daysExpired: -1 },
                    expiredSkuInfo: { skuType: ConsumerSkuType.NotDetected, daysExpired: -1 }
                });
            }

            if (this.capabilitiesService.capabilities.pwaEnabled) {
                this.configPromise = Promise.resolve(this.configParserService.parse<ConsumerInstallConfig>(ConsumerInstallConfigService.consumerInstallConfigElementId));
            } else {
                const tokensLoadedPromise = this.eventService.events.withScope(ObservableScope.PastAndFuture).pipe(
                    filter(event => event.name === AngularEvents.primaryTokensLoaded),
                    take(1))
                    .toPromise();
                const domReadyPromise = DOMInteractiveMarker.fullPageReady();
                this.configPromise = Promise.race([tokensLoadedPromise, domReadyPromise]).then(() => this.configParserService.parse<ConsumerInstallConfig>(ConsumerInstallConfigService.consumerInstallConfigElementId));
            }
        }
        return this.configPromise;
    }

    public getCurrentSku(): Promise<ConsumerSkuType> {
        return new Promise<ConsumerSkuType>( resolve => {
            this.getConfig()
            .then( config => resolve(config && config.currentSkuInfo ? config.currentSkuInfo.skuType : ConsumerSkuType.NotDetected))
            .catch(() => resolve( ConsumerSkuType.NotDetected));
        });
    }
}
